import React from 'react';

/**
 * Reusable tabbed chrome for an integration's detail page — Overview /
 * Connections / Resources / Activity, matching the layout used by the
 * Zilter CRM screenshots that this redesign is modeled on.
 *
 * Applied first to Meta Ads (the integration with the most real, working
 * logic), and meant to be reused as other integrations grow beyond a single
 * credential form (Resources = discovered pages/forms/numbers/agents,
 * Activity = a real log of sync/send/call events).
 *
 * Pass `tabs` as [{ id, label, count? }] and control the active tab from the
 * parent so each integration can decide which tabs it actually needs.
 */
export default function IntegrationDetailShell({ tabs, activeTab, onTabChange, headerRight, children }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,0.04)', borderRadius: 10, padding: 4 }}>
          {tabs.map(t => (
            <div
              key={t.id}
              onClick={() => onTabChange(t.id)}
              style={{
                padding: '7px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: activeTab === t.id ? '#fff' : 'transparent',
                color: activeTab === t.id ? '#0f172a' : '#64748b',
                boxShadow: activeTab === t.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {t.label}{t.count != null ? ` (${t.count})` : ''}
            </div>
          ))}
        </div>
        {headerRight && <div style={{ marginLeft: 'auto' }}>{headerRight}</div>}
      </div>
      {children}
    </div>
  );
}

// ─── Small building blocks shared across integration detail pages ──────────

export function StatusBadge({ connected, connectedLabel = '● Connected', disconnectedLabel = '○ Not Connected' }) {
  return (
    <span style={{
      padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: connected ? 'rgba(16,185,129,0.1)' : 'rgba(100,116,139,0.1)',
      color: connected ? '#10b981' : '#64748b',
      border: `1px solid ${connected ? 'rgba(16,185,129,0.25)' : 'rgba(100,116,139,0.2)'}`,
    }}>
      {connected ? connectedLabel : disconnectedLabel}
    </span>
  );
}

export function DetailRow({ label, value, mono = false }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: mono ? 11 : 13, color: '#0f172a', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: mono ? 'break-all' : 'normal' }}>{value}</div>
    </div>
  );
}

export function EmptyState({ icon = '📭', title, subtitle }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#475569', marginBottom: 4 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: '#94a3b8', maxWidth: 360, margin: '0 auto' }}>{subtitle}</div>}
    </div>
  );
}

export function ActivityRow({ icon = '🔄', title, subtitle, time }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(99,102,241,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{subtitle}</div>}
      </div>
      {time && <div style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>{time}</div>}
    </div>
  );
}
